import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { formatDateDisplay, formatPriceCents } from 'utils';
import { makeBookingManagerApi } from 'services/BookingManagerApi';
import { usePurchaseOrdersQueries, usePurchaseOrderDetailMap } from './usePurchaseOrdersQueries';
import { IPurchaseOrdersResponse } from 'services/BookingManagerApi/types/PurchaseOrdersResponse';
import { useDynamicParameters } from 'context/DynamicParameters/DynamicParametersContext';
import * as DashboardActions from 'store/modules/bookingManager/subdomains/dashboard/actions';
import * as DashboardSelectors from 'store/modules/bookingManager/subdomains/dashboard/selectors';
import * as AuthSelectors from 'store/modules/auth/selectors';
import TopNavigationBar from 'pureUi/TopNavigationBar';
import { Select } from 'ui/Select';
import { LoadingBar, ErrorBar } from 'ui/NetworkStatusBar';

import SupplierGroupAccordion from './SupplierGroupAccordion';
import { ENetworkRequestStatus, EUserType } from 'services/BackendApi';

import { bookingCurrencySymbolSelector } from 'store/modules/bookingManager/selectors';
import { useQueryClient } from '@tanstack/react-query';
interface IBookingManagerPurchaseOrdersProps extends RouteComponentProps {
  bookingUuid: string;
}

export const BookingManagerPurchaseOrders = ({ bookingUuid }: IBookingManagerPurchaseOrdersProps) => {
  const dispatch = useDispatch();
  const [selectedVersion, setSelectedVersion] = useState<{ version: number; timestamp: string } | null>(null);
  const [openAccordions, setOpenAccordions] = useState<Set<string>>(new Set());

  // Redux selectors
  const topNavigationData = useSelector(DashboardSelectors.topNavigationDataDashboardSelector);
  const dashboardNetworkRequests = useSelector(DashboardSelectors.dashboardNetworkRequestsSelector);
  const userRole = useSelector(AuthSelectors.getCurrentUserType);
  const bookingInformationReservationTeamData = useSelector(
    DashboardSelectors.bookingInformationReservationTeamDashboardSelector
  );
  const currencySymbol = useSelector(bookingCurrencySymbolSelector);
  const { dynamicParameters } = useDynamicParameters();
  const isPurchaseOrderReconciliationEnabled = dynamicParameters.ENABLE_PURCHASE_ORDER_RECONCILIATION === true;
  const queryClient = useQueryClient();
  // API setup
  const bookingManagerApi = makeBookingManagerApi();
  const purchaseOrdersQueries = usePurchaseOrdersQueries(bookingManagerApi, bookingUuid);

  // React Query hooks
  const versionListQuery = purchaseOrdersQueries.getPurchaseOrdersVersionList;
  const purchaseOrdersQuery = purchaseOrdersQueries.getPurchaseOrders(selectedVersion);
  const poDetails = usePurchaseOrderDetailMap(
    bookingManagerApi,
    purchaseOrdersQuery.data?.data ?? undefined,
    isPurchaseOrderReconciliationEnabled
  );

  // Load data on mount and reset/refresh everything
  useEffect(() => {
    dispatch(DashboardActions.getTopNavigationDataRequestAction(bookingUuid));
    setSelectedVersion(null);

    return () => {
      queryClient.resetQueries();
    };
  }, [bookingUuid]);

  // Set selected version to latest when version list loads or refreshes
  useEffect(() => {
    if (versionListQuery.data?.data) {
      const versions = versionListQuery.data.data;
      if (versions.length > 0) {
        const latestVersion = versions[0]; // Assuming first is latest
        // Always select the latest version when version list changes
        setSelectedVersion({
          version: latestVersion.version,
          timestamp: latestVersion.timestamp,
        });
      }
    }
  }, [versionListQuery.data]);

  const handleVersionChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const versions = versionListQuery.data?.data || [];
    const selectedVersionData = versions.find(v => v.version === parseInt(event.target.value));

    if (selectedVersionData) {
      setSelectedVersion({
        version: selectedVersionData.version,
        timestamp: selectedVersionData.timestamp,
      });
    }
  };

  // Summary data now comes directly from the API response

  const toggleAccordion = (key: string) => {
    const newOpenAccordions = new Set(openAccordions);
    if (newOpenAccordions.has(key)) {
      newOpenAccordions.delete(key);
    } else {
      newOpenAccordions.add(key);
    }
    setOpenAccordions(newOpenAccordions);
  };

  const renderVersionHistory = () => {
    // Only render if we have successfully loaded versions (not 404, not loading)
    if (versionListQuery.isLoading || versionListQuery.error) {
      return null;
    }

    const versions = versionListQuery.data?.data || [];
    const latestVersion = versions[0];

    // Don't render if no versions or no selected version
    if (versions.length === 0 || !selectedVersion || !latestVersion) {
      return null;
    }

    return (
      <div className="flex items-center">
        <span className="last-modified mb-2 self-center">
          Date last changed: {formatDateDisplay(new Date(parseInt(latestVersion.timestamp, 10)))}
        </span>
        <span className="ml-10px mb-2">
          <Select
            className="version-selector min-w-170px max-w-255px pl-6 pr-45px ml-4 text-ellipsis normal-case"
            value={String(selectedVersion.version)}
            onChange={handleVersionChange}
            options={versions.map(version => {
              const v = `V${version.version}, ${formatDateDisplay(new Date(parseInt(version.timestamp, 10)))}`;
              const u = version.user?.email;
              return {
                value: String(version.version),
                label: [v, u].filter(Boolean).join(' / '),
              };
            })}
          />
        </span>
      </div>
    );
  };

  const renderSummarySection = (responseData: IPurchaseOrdersResponse) => {
    return (
      <div className="summary-section bg-white border border-gray-20 rounded mb-6">
        <div className="flex items-stretch gap-10 p-5">
          {/* Total Purchase Orders */}
          <div className="flex flex-col items-center gap-1 flex-1">
            <div className="text-xs font-pt-sans font-normal text-gray-100 uppercase tracking-2xs text-center w-full">
              Total Purchase Orders
            </div>
            <div className="text-21px font-pt-sans font-bold text-green-prime text-right">
              {responseData.activePurchaseOrdersCount}
            </div>
          </div>

          {/* Vertical Divider */}
          <div className="w-0 border-l border-gray-20 self-stretch"></div>

          {/* Unassigned Purchase Orders */}
          <div className="flex flex-col items-center gap-1 flex-1">
            <div className="text-xs font-pt-sans font-normal text-gray-100 uppercase tracking-2xs text-center w-full">
              Unassigned Purchase Orders
            </div>
            <div className="text-21px font-pt-sans font-bold text-green-prime text-right">
              {responseData.activePurchaseOrdersWithoutSupplierCount}
            </div>
          </div>

          {/* Vertical Divider */}
          <div className="w-0 border-l border-gray-20 self-stretch"></div>

          {/* Total Cost */}
          <div className="flex flex-col items-center gap-1 flex-1">
            <div className="text-xs font-pt-sans font-normal text-gray-100 uppercase tracking-2xs text-center w-full">
              Total Cost
            </div>
            <div className="text-21px font-pt-sans font-bold text-green-prime text-right">
              {currencySymbol}
              {formatPriceCents(responseData.activePurchaseOrdersTotalCents)}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    // Check if version list returned 404 - means no purchase orders for this booking
    if (versionListQuery.error && (versionListQuery.error as any)?.response?.status === 404) {
      return (
        <div className="mt-8">
          <p className="text-gray-60">No Results Found.</p>
        </div>
      );
    }

    // Loading state
    if (versionListQuery.isLoading || (selectedVersion && purchaseOrdersQuery.isLoading) || poDetails.isLoading) {
      return <LoadingBar label="Loading purchase orders..." />;
    }

    // Other errors (not 404)
    if (versionListQuery.error || purchaseOrdersQuery.error) {
      return (
        <ErrorBar
          message="Failed to load purchase orders. Please try again."
          ctaCaption="Retry"
          onCtaClick={() => {
            versionListQuery.refetch();
            if (selectedVersion) {
              purchaseOrdersQuery.refetch();
            }
          }}
        />
      );
    }

    // We have versions but maybe no purchase orders data yet
    if (!selectedVersion) {
      return <LoadingBar label="Loading purchase orders..." />;
    }

    const responseData = purchaseOrdersQuery.data?.data;
    const supplierGroups = responseData?.supplierPurchaseOrderGroups || [];

    if (supplierGroups.length === 0) {
      return (
        <div className="bg-gray-10 border border-gray-30 rounded p-6 mt-8">
          <p className="text-gray-darker text-lg">No purchase orders found for this version.</p>
        </div>
      );
    }

    // Separate supplier groups with suppliers and unassigned groups
    const supplierGroupsWithSuppliers = supplierGroups.filter(group => group.supplierCode || group.supplierName);
    const unassignedGroups = supplierGroups.filter(group => !group.supplierCode && !group.supplierName);

    return (
      <div className="purchase-orders-content mt-8">
        {/* Summary Section */}
        {renderSummarySection(responseData!)}

        {/* Purchase Orders by Supplier */}
        {supplierGroupsWithSuppliers.length > 0 && (
          <>
            <h2 className="text-17px font-pt-sans font-bold text-black mb-10px">Purchase Orders by Supplier</h2>
            <div className="mb-6 space-y-2">
              {supplierGroupsWithSuppliers.map((supplierGroup, index) => {
                const accordionKey = `supplier-${supplierGroup.supplierCode || index}`;
                const isOpen = openAccordions.has(accordionKey);

                return (
                  <SupplierGroupAccordion
                    key={accordionKey}
                    supplierGroup={supplierGroup}
                    accordionKey={accordionKey}
                    isOpen={isOpen}
                    onToggle={() => toggleAccordion(accordionKey)}
                    isUnassigned={false}
                    currencySymbol={currencySymbol || ''}
                    isPurchaseOrderReconciliationEnabled={isPurchaseOrderReconciliationEnabled}
                    poDetailMap={poDetails.data}
                  />
                );
              })}
            </div>
          </>
        )}

        {/* Others... (Unassigned POs) */}
        {unassignedGroups.length > 0 && (
          <>
            <h2 className="text-17px font-pt-sans font-bold text-black mb-10px">Others...</h2>
            <div className="space-y-2">
              {unassignedGroups.map((supplierGroup, index) => {
                const accordionKey = `unassigned-${index}`;
                const isOpen = openAccordions.has(accordionKey);

                return (
                  <SupplierGroupAccordion
                    key={accordionKey}
                    supplierGroup={supplierGroup}
                    accordionKey={accordionKey}
                    isOpen={isOpen}
                    onToggle={() => toggleAccordion(accordionKey)}
                    isUnassigned={true}
                    currencySymbol={currencySymbol || ''}
                    isPurchaseOrderReconciliationEnabled={isPurchaseOrderReconciliationEnabled}
                    poDetailMap={poDetails.data}
                  />
                );
              })}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <React.Fragment>
      <TopNavigationBar
        userRole={userRole as EUserType}
        data={topNavigationData}
        bookingInformationReservationTeamData={bookingInformationReservationTeamData}
        isLoading={dashboardNetworkRequests.topNavigationDataLoad === ENetworkRequestStatus.PENDING}
        isError={dashboardNetworkRequests.topNavigationDataLoad === ENetworkRequestStatus.ERROR}
      />

      <div className="purchase-orders font-pt-sans mb-110px">
        <div className="header flex justify-between">
          <h1 className="text-4xl font-noe-display font-normal mt-0 mb-2 self-center">Purchase Orders</h1>
          {renderVersionHistory()}
        </div>

        {renderContent()}
      </div>
    </React.Fragment>
  );
};

export default BookingManagerPurchaseOrders;
